---
title: My thoughts on testing software
coverImage: /blog/assets/images/testing.webp
date: 2023-11-14T23:12:25.000Z
updated: 2023-11-21T18:18:52.000Z
excerpt: How I think about testing, and decide what tests to write
hidden: false
tags:
  - TestingW
  - SDLC
---

<script>import SparklingHighlight from "@lib/components/molecules/SparklingHighlight.svelte";</script>

# Testing

For any serious project, testing is vital to velocity. Sure, you can get an initial implementation working with manual testing. But are you going to manually test all of the functionality every time to make a significant change? Even the little changes? No, you'll test what you need to get the thing working and eventually you'll change something that breaks something else. If you're lucky it'll be obvious right away. If you're unlucky, you'll notice a month and 12 major PRs later, and have to figure out which of those edits caused the issue. Luckily your project is well encapsulated so you can isolate the bug easily.

Anyways, of course there is a better way. <SparklingHighlight>Testing!</SparklingHighlight>

## How do I test?

My stance on testing is to test things that are vital and stable.

### Vital Features

A Feature is vital if the app needs it to run, without these features the app will be un-usable. These are no-brainers to test, since they're usually core mechanics and frameworks that enable everything else to work.

If a feature isn't vital, it can still be useful to test it if it's not too much work, but if you have a difficult testing framework or the behavior is complex, there's probably something else vital without tests or a new feature you can get a head start on. If a component is shifted a few pixels or a notification is late by a few seconds it's not the end of the world.

### Stable Features

A feature is stable if it is not modified often. These are the kinds of features that have existed since the app was created, were built efficiently, extensible, and encapsulated enough that no one has had to touch them in years. These are perfect things to test, since you want them to keep working like they have been, and that's exactly what testing ensures.

If a feature isn't stable, it's going to be changed somewhat frequently. Having a test for it will still prevent changes elsewhere from breaking it unexpectedly. However, when you change the feature itself and the behavior _should_ change, then you'll have to modify the test, and when you modify the test you're just as likely to make a mistake as you are changing the feature.

## Tests should not change

Tests should describe the way that your code runs, yes, but the code describes that as well. Using tests to describe the functionality is, in my mind, lazy programming. The code should be readable. Tests are there as a confirmation. If you change them all the time then they describe how you imagine the code should work, not how it does.

The ideal test should describe all the invariants. Everything that is the same regardless of the invocation, parameters, etc.
This also touches on the advice that code should be open to extension and closed to modification. If you can, it makes things a lot simpler to maintain if the core functionality stays the same.

### TDD

This is kind of the idea with TDD. The only issue I see with writing tests before hand is you don't always know how it's going to work when you start. Admitedly, I haven't tried TDD for long enough to adapt my thinking to it. I think I would just have to only pre-write tests for functionality that must be a certain way.

I'm not sure how I would do that without having to add tests afterwards though, since I like to test the internals as well. Although the inner workings of encapsulated code should not have outside effects, sometimes they do. Also, it's important to verify there are no memory issues or performance issues regardless of invocation.

Maybe pre-coding TDD tests can describe the expected input/output, and more detailed internal/unit testing can be completed afterwards.
